import type {
  EpsonPasswordCheckResult,
  EpsonPrinterInfo,
} from "@app/vue/providers/bridge-rpc-types";
import { WifiUnsupportedError } from "./printerWifi";

// Series the native module (expo-epson-printer) knows how to open.
const EPSON_SERIES = [
  "TM_M10",
  "TM_M30",
  "TM_M30II",
  "TM_M30III",
  "TM_M50",
  "TM_M50II",
  "TM_M55",
  "TM_P20",
  "TM_P20II",
  "TM_P60",
  "TM_P60II",
  "TM_P80",
  "TM_P80II",
  "TM_T20",
  "TM_T60",
  "TM_T70",
  "TM_T81",
  "TM_T82",
  "TM_T83",
  "TM_T83III",
  "TM_T88",
  "TM_T88VII",
  "TM_T90",
  "TM_T90KP",
  "TM_T100",
  "TM_U220",
  "TM_U220II",
  "TM_U330",
  "TM_L90",
  "TM_L90LFC",
  "TM_L100",
  "TM_H6000",
  "TS_100",
  "EU_M30",
  "SB_H50",
  "SB_M30",
] as const;

export type EpsonSeries = (typeof EPSON_SERIES)[number];

export const DEFAULT_EPSON_SERIES: EpsonSeries = "TM_M30III";

/**
 * Epson printers advertise names like "TM-m30III_012345" over Bluetooth.
 * Pick the longest known series that prefixes the normalized name so
 * "TM-m30III" doesn't match "TM_M30".
 */
export const detectEpsonSeries = (deviceName: string): EpsonSeries => {
  const normalized = deviceName
    .trim()
    .toUpperCase()
    .replace(/[-\s]+/g, "_");
  let match: EpsonSeries | null = null;
  for (const series of EPSON_SERIES) {
    if (!normalized.startsWith(series)) continue;
    const next = normalized.charAt(series.length);
    if (next && /[A-Z0-9]/.test(next)) continue;
    if (!match || series.length > match.length) match = series;
  }
  return match ?? DEFAULT_EPSON_SERIES;
};

export type SavedBluetoothPrinter = {
  target: string;
  deviceName: string;
  series: EpsonSeries;
  // Admin password needed by get/setPrinterSettingEx; null when the printer
  // has none, doesn't support one, or the user skipped the step.
  adminPassword: string | null;
};

const STORAGE_KEY = "lemmon.bluetoothPrinter";

export const saveBluetoothPrinter = (
  printer: EpsonPrinterInfo,
  series: EpsonSeries,
  adminPassword: string | null,
): SavedBluetoothPrinter => {
  const saved = {
    target: printer.target,
    deviceName: printer.deviceName,
    series,
    adminPassword,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
  } catch (error) {
    console.warn("Failed to save Bluetooth printer:", error);
  }
  return saved;
};

export const getSavedBluetoothPrinter = (): SavedBluetoothPrinter | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SavedBluetoothPrinter) : null;
  } catch {
    return null;
  }
};

export type SetupStage =
  | "discover"
  | "connect"
  | "status"
  | "password"
  | "test_print"
  | "wifi"
  | "network_search"
  | "register";

export type SetupErrorKind =
  | "no_bridge"
  | "unsupported_platform"
  | "permission"
  | "location_disabled"
  | "discover"
  | "connect"
  | "status"
  | "password"
  | "password_invalid"
  | "test_print"
  | "wifi"
  | "wifi_unsupported"
  | "network_search"
  | "register";

export type SetupError = {
  kind: SetupErrorKind;
  // Raw native message, shown as detail so failures stay diagnosable.
  detail: string;
};

/**
 * Classify by the stage that failed; only a few native messages are specific
 * enough to override that (see EpsonPrinterModule.kt).
 */
export const classifySetupError = (
  stage: SetupStage,
  error: unknown,
): SetupError => {
  const detail =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  const message = detail.toLowerCase();

  if (error instanceof WifiUnsupportedError) {
    return { kind: "wifi_unsupported", detail: "" };
  }
  if (message.includes("android-only")) {
    return { kind: "unsupported_platform", detail };
  }
  if (message.includes("permission")) {
    return { kind: "permission", detail };
  }
  if (message.includes("location services")) {
    return { kind: "location_disabled", detail };
  }
  return { kind: stage, detail };
};

/**
 * App builds before the verifyPassword change resolve true/false, where false
 * can also mean "no password set"; treat it as a rejection the user can skip.
 */
export const normalizePasswordCheck = (
  result: EpsonPasswordCheckResult | boolean,
): EpsonPasswordCheckResult => {
  if (result === true) return "OK";
  if (result === false) return "INVALID_PASSWORD";
  return result;
};
